/**
 * PermissionProvider + useCan (PERM-01) — the client side of the RBAC framework.
 *
 * Loads the signed-in user's role (+ per-user grant/deny overrides) once, resolves
 * their effective capability set, and exposes `can(key)` to the whole app. Admin
 * is a wildcard: `can()` is always true for an admin, including for capabilities
 * added in the future. Gate any action with `useCan("module.action")`.
 *
 * Enforcement note: this is the CLIENT gate (hide/disable UI). The server-side
 * gate is `pegasus_can()` in Postgres for RLS on data tables (PERM-04, applied as
 * we tighten each table from "any authenticated" to "authenticated AND capable").
 * Until then we FAIL OPEN (below) so the framework can be wired everywhere without
 * risking locking the single admin user out mid-build.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";

/**
 * During the framework's infancy (one admin user, no per-table RLS yet) we allowed
 * when permissions were unknown — so wiring `useCan` everywhere couldn't hide
 * things from the owner. PERM-ENFORCE-01c (2026-11-03): flipped to `false`. A
 * login with no app_user row now holds nothing (the office shell shows "Not for
 * This Login" with Sign Out); the admin wildcard is untouched; every route is
 * behind RequireCap (routeCaps.ts) and the rail/palette read the same map. */
const FAIL_OPEN = false;

interface PermState {
  ready: boolean;
  isAdmin: boolean;
  roleKey: string | null;
  caps: Set<string>;
}

interface PermissionValue {
  can: (key: string) => boolean;
  /** True when the login holds ANY of the keys (route guards, settings groups). */
  canAny: (keys: readonly string[]) => boolean;
  isAdmin: boolean;
  ready: boolean;
  roleKey: string | null;
}

const PermissionContext = createContext<PermissionValue | undefined>(undefined);

export function PermissionProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [state, setState] = useState<PermState>({
    ready: false,
    isAdmin: false,
    roleKey: null,
    caps: new Set(),
  });

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setState({ ready: true, isAdmin: false, roleKey: null, caps: new Set() });
      return;
    }
    (async () => {
      const { data: u } = await supabase
        .from("app_user")
        .select("role_key, grant_extra, deny")
        .eq("user_id", user.id)
        .maybeSingle();
      if (cancelled) return;
      if (!u) {
        // User not seeded yet — no capabilities at all (FAIL_OPEN is false).
        setState({ ready: true, isAdmin: false, roleKey: null, caps: new Set() });
        return;
      }
      const { data: r } = await supabase
        .from("app_role")
        .select("is_admin, capabilities")
        .eq("key", (u as { role_key: string }).role_key)
        .maybeSingle();
      if (cancelled) return;
      const isAdmin = Boolean(r?.is_admin);
      const caps = new Set<string>([
        ...(((r?.capabilities as string[]) ?? []) || []),
        ...(((u as { grant_extra?: string[] }).grant_extra ?? []) || []),
      ]);
      for (const d of (u as { deny?: string[] }).deny ?? []) caps.delete(d);
      setState({
        ready: true,
        isAdmin,
        roleKey: (u as { role_key: string }).role_key,
        caps,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const can = useCallback(
    (key: string) => {
      if (state.isAdmin) return true; // wildcard — incl. future capabilities
      if (!state.ready || !state.roleKey) return FAIL_OPEN; // unknown → see flag
      if (state.caps.has(key)) return true;
      // PERM-ENFORCE-01: `settings.manage` is the umbrella over the six
      // `settings.<group>` keys (mirrored in the database's pegasus_can()).
      if (key.startsWith("settings.") && state.caps.has("settings.manage")) return true;
      return false;
    },
    [state],
  );
  const canAny = useCallback((keys: readonly string[]) => keys.some((k) => can(k)), [can]);

  return (
    <PermissionContext.Provider
      value={{ can, canAny, isAdmin: state.isAdmin, ready: state.ready, roleKey: state.roleKey }}
    >
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermissions(): PermissionValue {
  const ctx = useContext(PermissionContext);
  if (!ctx)
    throw new Error("usePermissions must be used within a PermissionProvider");
  return ctx;
}

/** Gate any action: `const canReassign = useCan("dispatch.reassign");` */
export function useCan(key: string): boolean {
  return usePermissions().can(key);
}

/** Any-of gate: `useCanAny(settingsCaps("Money"))`. */
export function useCanAny(keys: readonly string[]): boolean {
  return usePermissions().canAny(keys);
}
